// The timesheet's rules (Back office sweep, Oct 2026: D2, mock "Timesheets"). A shift's times
// are the venue's wall clock on its day; one that ends earlier than it starts ends the next
// morning.

const at = (date, hhmm) => new Date(`${date}T${hhmm || '00:00'}:00`);
const startOf = (s) => at(s.date, s.start_time);
const endOf = (s) => {
  const e = at(s.date, s.end_time);
  if (e <= startOf(s)) e.setDate(e.getDate() + 1);
  return e;
};
const hoursBetween = (a, b) => Math.max(0, (b - a) / 3600000);

// "6h 05m"
export const duration = (hours) => {
  const mins = Math.round((hours || 0) * 60);
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
};

const WEEKDAY = (date) => new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long' });

// What should have been worked by now, against what was: only the part of each shift that
// should have happened yet, so a Wednesday compares Monday to Wednesday-so-far, not the week.
export function soFar(shifts, now = new Date()) {
  let scheduled = 0;
  let worked = 0;
  for (const s of shifts) {
    const start = startOf(s);
    if (start > now) continue;
    scheduled += hoursBetween(start, new Date(Math.min(endOf(s), now)));
    if (s.status === 'in_progress' && s.actual_start) {
      // Still clocked in: counted up to now, or to the rota's finish for a shift the till
      // left open (it reads "No clock-out" until a manager sets the real time).
      const began = at(s.date, s.actual_start);
      worked += began <= now ? hoursBetween(began, new Date(Math.min(now, endOf(s)))) : 0;
    } else {
      worked += s.actual_hours || 0;
    }
  }
  return { scheduled: Math.round(scheduled * 10) / 10, worked: Math.round(worked * 10) / 10 };
}

// How one shift went, in a few quiet words under its times.
export function cellStatus(s, now = new Date()) {
  const start = startOf(s);
  const end = endOf(s);
  if (start > now && s.status === 'scheduled') return { future: true, words: '' };
  const adjusted = s.adjusted ? ' · adjusted' : '';
  if (s.status === 'completed') {
    const early = (s.actual_hours || 0) < (s.hours || 0) - 0.25;
    return { words: `${duration(s.actual_hours)}${early ? ' · left early' : ''}${adjusted}`, tone: early ? 'warn' : null };
  }
  if (s.status === 'in_progress') {
    // Still on well after the rota's finish: the till kept the shift open.
    if (now - end > 2 * 3600000) return { words: 'No clock-out', tone: 'warn' };
    return { words: `On shift since ${s.actual_start}`, tone: 'live' };
  }
  return end < now ? { words: 'No clock-in', tone: 'warn' } : { words: 'Not started' };
}

// The shifts that need a manager to look: a shift left open, or one nobody clocked into,
// once it's over.
export function problems(shifts, now = new Date(), byStaff = {}) {
  const out = [];
  for (const s of shifts) {
    const st = cellStatus(s, now);
    const name = s.staff_name || byStaff[s.staff_id]?.name || 'Someone';
    if (st.words === 'No clock-out') {
      out.push({ shift: s, title: `${name} has no clock-out on ${WEEKDAY(s.date)}.`, body: 'The till kept the shift open; set when they finished.' });
    } else if (st.words === 'No clock-in') {
      out.push({ shift: s, title: `${name} didn't clock in on ${WEEKDAY(s.date)}.`, body: "If they worked it, set their times; if they didn't, leave it." });
    }
  }
  return out.sort((a, b) => (a.shift.date + a.shift.start_time).localeCompare(b.shift.date + b.shift.start_time));
}

// Kitchen or front of house, from the job title.
export const kitchenRole = (role) => /chef|cook|kitchen|porter|commis|\bkp\b/i.test(role || '');
